import { Input } from 'antd';
import type { ComponentProps, ReactNode } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { FormField } from '../form-field';

import styles from './text-field.module.scss';

type AntdInputProps = Omit<
  ComponentProps<typeof Input>,
  'value' | 'onChange' | 'defaultValue' | 'prefix' | 'suffix' | 'size'
>;

export type TextFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = AntdInputProps & {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  help?: string;
  required?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  /** Figma heights: default = 40px, tall = 46px. */
  fieldSize?: 'default' | 'tall';
};

function affix(node: ReactNode) {
  return typeof node === 'string' ? <span className={styles.affixText}>{node}</span> : node;
}

export function TextField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  name,
  control,
  label,
  help,
  required,
  prefix,
  suffix,
  fieldSize = 'default',
  type,
  className,
  autoFocus,
  status: externalStatus,
  ...rest
}: TextFieldProps<TFieldValues, TName>) {
  const InputComponent = type === 'password' ? Input.Password : Input;
  return (
    <FormField name={name} control={control} label={label} help={help} required={required}>
      {({ field, fieldState }) => (
        <InputComponent
          id={name}
          autoFocus={autoFocus}
          size={fieldSize === 'tall' ? 'large' : 'middle'}
          prefix={affix(prefix)}
          suffix={affix(suffix)}
          type={type === 'password' ? undefined : type}
          className={[styles.input, className].filter(Boolean).join(' ')}
          {...rest}
          // After {...rest} and field-error first, so an external status prop can't
          // clobber the validation error (and vice versa).
          status={fieldState.error ? 'error' : externalStatus}
          value={field.value ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          ref={field.ref}
        />
      )}
    </FormField>
  );
}
